import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTag,
  faCheckCircle,
  faEye,
  faTicketAlt,
  faCamera,
  faUser,
  faClock,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge } from './tagStatusHelper';
import TagLinkedTicketsSection from './TagLinkedTicketsSection';
import TagEvidenceSection from './TagEvidenceSection';
import TagEditForm from './TagEditForm';
import { formatDateTime } from '../../../utils';

/**
 * Modal unificado de Detalle (Vista) y Edición de Incidencia
 */
const TagDetailModal = ({
  isOpen,
  selectedTag,
  isEditMode,
  setIsEditMode,
  formName,
  setFormName,
  formDescription,
  setFormDescription,
  formStatus,
  setFormStatus,
  isSaving,
  onSave,
  onClose,
  onOpenComplete,
  isDark = false,
  tagTickets = [],
  isLoadingTagTickets = false,
  photosList = [],
  isLoadingPhotos = false,
  isUploadingPhoto = false,
  photoDescription = '',
  setPhotoDescription,
  selectedPhotoPreview = null,
  onDiscardPhotoPreview,
  onSavePhotoPreview,
  isUploadOpen = false,
  setIsUploadOpen,
  fileInputRef,
  onFileSelect,
  onOpenLightbox,
  onOpenDeletePhoto
}) => {
  if (!isOpen) return null;

  const statusInfo = getStatusBadge(selectedTag?.iEstadoIncidencia || formStatus, isDark);

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}
      tabIndex="-1"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{ maxWidth: '760px', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="modal-content border shadow-lg"
          style={{
            backgroundColor: isDark ? '#182635' : '#ffffff',
            borderColor: isDark ? '#233142' : '#cbd5e1',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header del Modal */}
          <div
            className="modal-header border-bottom px-4 py-3 d-flex align-items-center justify-content-between"
            style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
          >
            <div className="d-flex align-items-center gap-2.5">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                  color: '#2563eb'
                }}
              >
                <FontAwesomeIcon icon={!selectedTag ? faPlus : (isEditMode ? faEdit : faTag)} style={{ fontSize: '0.95rem' }} />
              </div>
              <div>
                <h6 className="modal-title fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  <span>{!selectedTag ? 'Nueva Incidencia' : (isEditMode ? 'Editar Incidencia' : selectedTag.vNombre)}</span>
                  {selectedTag && !isEditMode && (
                    <span
                      className="badge px-2.5 py-0.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1"
                      style={{
                        backgroundColor: statusInfo.bg,
                        color: statusInfo.color,
                        border: `1px solid ${statusInfo.border}`,
                        fontSize: '0.72rem'
                      }}
                    >
                      <FontAwesomeIcon icon={statusInfo.icon} className="me-1" style={{ fontSize: '0.65rem' }} />
                      <span>{statusInfo.label}</span>
                    </span>
                  )}
                </h6>
                <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
                  {selectedTag ? `Incidencia ID #${selectedTag.idEtiqueta}` : 'Crea una nueva etiqueta para clasificar tickets'}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {selectedTag && !isEditMode && (
                <>
                  {Number(selectedTag.iEstadoIncidencia) !== 3 && Number(selectedTag.iTotalTickets) > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success rounded-pill px-3 fw-semibold d-flex align-items-center gap-1.5 shadow-none"
                      onClick={() => onOpenComplete(selectedTag)}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      <span>Resolver</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-semibold d-flex align-items-center gap-1.5 shadow-none"
                    onClick={() => setIsEditMode(true)}
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                    <span>Editar</span>
                  </button>
                </>
              )}

              {selectedTag && isEditMode && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-semibold d-flex align-items-center gap-1.5 shadow-none"
                  onClick={() => {
                    setFormName(selectedTag.vNombre || '');
                    setFormDescription(selectedTag.vDescripcion || '');
                    setFormStatus(Number(selectedTag.iEstadoIncidencia) || 1);
                    setIsEditMode(false);
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  <span>Ver Detalle</span>
                </button>
              )}

              <button
                type="button"
                className="btn-close shadow-none ms-1"
                style={{ filter: isDark ? 'invert(1) grayscale(100%)' : 'none' }}
                onClick={onClose}
              />
            </div>
          </div>

          {/* Body del Modal */}
          <div className="modal-body px-4 py-3" style={{ overflowY: 'auto' }}>
            {!isEditMode && selectedTag ? (
              <div>
                {/* Resumen y Métricas */}
                <div
                  className="p-3 rounded-3 border mb-3"
                  style={{
                    backgroundColor: isDark ? '#101c26' : '#f8fafc',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                  }}
                >
                  <div className="row g-2 text-center text-md-start align-items-center">
                    <div className="col-12 col-md-4">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: '34px',
                            height: '34px',
                            backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
                            color: '#6366f1'
                          }}
                        >
                          <FontAwesomeIcon icon={faTicketAlt} style={{ fontSize: '0.85rem' }} />
                        </div>
                        <div>
                          <div className="small fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                            {selectedTag.iTotalTickets || 0} Tickets
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            Vinculados a esta incidencia
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: '34px',
                            height: '34px',
                            backgroundColor: isDark ? 'rgba(14, 165, 233, 0.2)' : '#e0f2fe',
                            color: '#0ea5e9'
                          }}
                        >
                          <FontAwesomeIcon icon={faCamera} style={{ fontSize: '0.85rem' }} />
                        </div>
                        <div>
                          <div className="small fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                            {photosList.length} Evidencias
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            Fotos adjuntas
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="small text-muted" style={{ fontSize: '0.74rem' }}>
                        <div>
                          <FontAwesomeIcon icon={faUser} className="me-1 opacity-75" />
                          Creado por: <strong style={{ color: isDark ? '#cbd5e1' : '#334155' }}>{selectedTag.vCreatedUser || 'Sistema'}</strong>
                        </div>
                        {selectedTag.dCreatedDate && (
                          <div className="mt-0.5">
                            <FontAwesomeIcon icon={faClock} className="me-1 opacity-75" />
                            {formatDateTime(selectedTag.dCreatedDate)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notas y Descripción */}
                <div className="mb-4">
                  <label className="form-label small fw-bold mb-1.5 d-flex align-items-center gap-1.5" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                    <span>Notas y Descripción</span>
                  </label>
                  <div
                    className="p-3 rounded-3 border"
                    style={{
                      backgroundColor: isDark ? '#101c26' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#cbd5e1',
                      color: isDark ? '#cbd5e1' : '#334155',
                      fontSize: '0.86rem',
                      minHeight: '70px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {selectedTag.vDescripcion || (
                      <span className="fst-italic opacity-50">No hay notas o descripción registradas para esta incidencia.</span>
                    )}
                  </div>
                </div>

                {/* Sección de Tickets Vinculados */}
                <TagLinkedTicketsSection
                  tagTickets={tagTickets}
                  isLoadingTagTickets={isLoadingTagTickets}
                  isDark={isDark}
                />

                {/* Sección de Fotos y Evidencias */}
                <TagEvidenceSection
                  photosList={photosList}
                  isLoadingPhotos={isLoadingPhotos}
                  isUploadingPhoto={isUploadingPhoto}
                  photoDescription={photoDescription}
                  setPhotoDescription={setPhotoDescription}
                  selectedPhotoPreview={selectedPhotoPreview}
                  onDiscardPhotoPreview={onDiscardPhotoPreview}
                  onSavePhotoPreview={onSavePhotoPreview}
                  isUploadOpen={isUploadOpen}
                  setIsUploadOpen={setIsUploadOpen}
                  fileInputRef={fileInputRef}
                  onFileSelect={onFileSelect}
                  onOpenLightbox={onOpenLightbox}
                  onOpenDeletePhoto={onOpenDeletePhoto}
                  isDark={isDark}
                />
              </div>
            ) : (
              /* Modo Edición / Creación */
              <div>
                <TagEditForm
                  selectedTag={selectedTag}
                  formName={formName}
                  setFormName={setFormName}
                  formDescription={formDescription}
                  setFormDescription={setFormDescription}
                  formStatus={formStatus}
                  setFormStatus={setFormStatus}
                  onSave={onSave}
                  isDark={isDark}
                />

                {/* Sección de Fotos y Evidencias en Creación y Edición */}
                <TagEvidenceSection
                  photosList={photosList}
                  isLoadingPhotos={isLoadingPhotos}
                  isUploadingPhoto={isUploadingPhoto}
                  photoDescription={photoDescription}
                  setPhotoDescription={setPhotoDescription}
                  selectedPhotoPreview={selectedPhotoPreview}
                  onDiscardPhotoPreview={onDiscardPhotoPreview}
                  onSavePhotoPreview={onSavePhotoPreview}
                  isUploadOpen={isUploadOpen}
                  setIsUploadOpen={setIsUploadOpen}
                  fileInputRef={fileInputRef}
                  onFileSelect={onFileSelect}
                  onOpenLightbox={onOpenLightbox}
                  onOpenDeletePhoto={onOpenDeletePhoto}
                  isDark={isDark}
                />
              </div>
            )}
          </div>

          {/* Footer del Modal */}
          <div
            className="modal-footer border-top px-4 py-2.5 d-flex align-items-center justify-content-end gap-2"
            style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
          >
            {!isEditMode && selectedTag ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill px-3 shadow-none"
                onClick={onClose}
              >
                Cerrar
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm rounded-pill px-3 py-1.5 shadow-none"
                  style={{
                    fontSize: '0.84rem',
                    color: isDark ? '#94a3b8' : '#64748b',
                    border: isDark ? '1px solid #233142' : '1px solid #cbd5e1',
                    backgroundColor: 'transparent'
                  }}
                  onClick={() => {
                    if (selectedTag) {
                      setFormName(selectedTag.vNombre || '');
                      setFormDescription(selectedTag.vDescripcion || '');
                      setFormStatus(Number(selectedTag.iEstadoIncidencia) || 1);
                      setIsEditMode(false);
                    } else {
                      onClose();
                    }
                  }}
                  disabled={isSaving}
                >
                  {selectedTag ? 'Cancelar Edición' : 'Cancelar'}
                </button>
                <button
                  type="submit"
                  form="form-incidencia"
                  className="btn btn-sm btn-primary rounded-pill px-4 py-1.5 shadow-none d-flex align-items-center justify-content-center fw-semibold"
                  style={{
                    gap: '8px',
                    fontSize: '0.84rem',
                    backgroundColor: '#2563eb',
                    borderColor: '#2563eb'
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-1" style={{ fontSize: '0.85rem' }} />
                      <span>{selectedTag ? 'Guardar Cambios' : 'Crear Incidencia'}</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagDetailModal;
